import React from 'react'
import { observer } from 'mobx-react'
import { Link } from 'react-router-dom'
import { Icon } from '@/ui/icon'
import cx from 'classnames'
import Spinner from '@/ui/Spinner'

type CTAVariant = 'default' | 'system' | 'gradient' | 'destructive'

interface CTAProps {
  className?: string
  variant?: CTAVariant
  size?: 'small' | 'medium' | 'large'
  isDisabled?: boolean
  isLoading?: boolean
  onClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void
  to?: string
  iconLeftName?: string
  iconRightName?: string
  type?: 'button' | 'submit' | 'reset'
  form?: string
  children?: React.ReactNode
  tabIndex?: number
}

const CTA = ({
  className,
  variant = 'default',
  size = 'small',
  isDisabled = false,
  isLoading = false,
  onClick,
  type = 'button',
  form,
  to,
  iconLeftName,
  iconRightName,
  children,
  tabIndex,
}: CTAProps) => {
  if (!to && !onClick && type !== 'submit') {
    console.error('CTA: Supply at least one of "to" or "onClick"')
    return null
  }

  const content = (
    <>
      {iconLeftName && (
        <Icon size={14} name={iconLeftName} className="cta-iconLeft" />
      )}
      {children && <span className="cta-text">{children}</span>}
      {iconRightName && (
        <Icon size={14} name={iconRightName} className="cta-iconRight" />
      )}
    </>
  )

  if (to) {
    return (
      <Link
        className={cx('cta', className, variant, size, {
          isLoading,
          isDisabled,
        })}
        to={to}
      >
        {isLoading ? <Spinner isVisible /> : content}
      </Link>
    )
  }

  return (
    <button
      className={cx('cta', className, variant, size, { isDisabled, isLoading })}
      onClick={onClick}
      type={type}
      form={form}
      disabled={isDisabled || isLoading}
      tabIndex={tabIndex || isDisabled ? -1 : undefined}
    >
      {isLoading ? <Spinner isVisible /> : content}
    </button>
  )
}

export default observer(CTA)
